import { isPlatformBrowser, Location as PageLocation, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  computed,
  input,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerInput, MatDatepicker } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'avn/modules/services/api.service';
import { GoogleMapsLoaderService } from 'avn/modules/services/google-maps-loader.service';
import { StoreService } from 'avn/modules/services/store.service';
import { toUrlSlug } from 'avn/modules/services/utils/url-slug.util';
import { SearchBoxPopComponent } from 'avn/modules/shared/components/search-box/search-box-pop/search-box-pop';
import { InputSafetyDirective } from 'avn/modules/shared/directives/input-safety.directive';
import {
  IAvaanaCategory,
  Location,
  ILocationDropdownList,
  IPopularServiceDatum,
  Prediction
} from 'avn/modules/shared/interfaces/homepage';
import { HighlightPipe } from 'avn/modules/shared/pipes/highlight.pipe';
import { SlugTextPipe, SlugTextPipe as SlugTextPipe_1 } from 'avn/modules/shared/pipes/slug-text.pipe';
import moment from 'moment';
import { Subject, Subscription, debounceTime } from 'rxjs';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

const LOCATION_RETRY_INITIAL_DELAY_MS = 10;
const LOCATION_RETRY_DELAY_MS = 500;
const LOCATION_TEXT_DEBOUNCE_MS = 500;
const MAX_LOCATION_RETRIES = 5;
const LOCALITY_TYPES_MIN_LENGTH_LEVEL_2 = 2;
const LOCALITY_TYPES_MIN_LENGTH_LEVEL_3 = 3;
const LOCALITY_TYPE_INDEX_LEVEL_3 = 2;

@Component({
  selector: 'app-search-box',
  templateUrl: './search-box.html',
  styleUrls: ['./search-box.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
  ],
  imports: [
    NgClass,
    InputSafetyDirective,
    FormsModule,
    MatDatepickerInput,
    MatDatepicker,
    SlugTextPipe_1,
    HighlightPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];

  public readonly dropdownType = signal('');
  public todayDate = new Date();
  public readonly popularCategories = signal<IPopularServiceDatum[]>([]);
  public allServices: IAvaanaCategory[] = [];
  public readonly serviceText = signal('');
  public readonly locationText = signal('');
  // businessList: BusinessSearchList[] = [];
  // healthConditions: HealthConditionList[] = [];
  public readonly searchType = signal('');
  public locationTextChange = new Subject<string>();
  public readonly filteredLocationsList = signal<ILocationDropdownList[]>([]);
  public searchSlug = '';
  public locationSlug = '';
  public postCodeSlug = '';
  public readonly selectedDate = signal(moment(this.todayDate));
  public readonly pageType = signal(false);
  public currentPage = '';
  public extraParams: any;
  public currentView = 'list';
  public retryed = 0;
  public toSetLocation = new Location();
  public readonly fetchingLocation = signal(false);

  public readonly showBack = input(true);
  public readonly viewType = input('normal');
  public readonly label = input('city, state');
  public readonly minDate = input<string>();
  public readonly isMobile = input<boolean>();
  @ViewChild('locationInput') public locationInput!: ElementRef;

  public constructor(
    private readonly api: ApiService,
    private readonly store: StoreService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly slugText: SlugTextPipe,
    private readonly dialog: MatDialog,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly pageLocation: PageLocation,
    private readonly cd: ChangeDetectorRef,
    private readonly googleMapsLoader: GoogleMapsLoaderService
  ) {}

  public ngOnInit(): void {
    this.extraParams = this.activatedRoute.snapshot.queryParams;
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('click', this.documentClickListener);
    }

    const searchBoxParamsSub = this.store.searchBoxParams.subscribe((params: any) => {
      const urlSplit = this.router.url.split('/');
      this.pageType.set(
        ['search', 'service', 'category', 'symptom', 'browse', 'assistedbooking'].includes(urlSplit[1])
      );
      this.currentPage = urlSplit[1];
      if (this.pageType()) {
        if (urlSplit[1] === 'category') {
          this.serviceText.set(this.slugText.transform(params['category_name'], 'capitalize'));
        } else {
          this.serviceText.set(this.slugText.transform(params['service'], 'capitalize'));
        }
      }
      this.cd.detectChanges();
    });
    this.subscriptions.push(searchBoxParamsSub);

    const queryParamsSub = this.activatedRoute.queryParams.subscribe((params: any) => {
      if (params?.['dt']) {
        this.selectedDate.set(moment(params?.['dt'], 'YYYY-MM-DD'));
        this.cd.markForCheck();
      }
    });
    this.subscriptions.push(queryParamsSub);

    const meraLocationSub = this.store.meraLocationSub.subscribe(() => {
      setTimeout(() => {
        this.setLocation();
      }, LOCATION_RETRY_INITIAL_DELAY_MS);
    });
    this.subscriptions.push(meraLocationSub);

    const locationTextSub = this.locationTextChange.pipe(debounceTime(LOCATION_TEXT_DEBOUNCE_MS)).subscribe((value) => {
      this.getPlaces(value);
    });
    this.subscriptions.push(locationTextSub);

    if (isPlatformBrowser(this.platformId)) {
      this.fetchPopularCategories();
      this.fetchAllServices();
      // this.fetchAllBusinessList();
      // this.fetchAllHealthConditions();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.documentClickListener);
    }
  }

  public getPageType(): boolean {
    const urlSplit = this.router.url.split('/');
    return ['search', 'service', 'category', 'symptom', 'browse', 'assistedbooking'].includes(urlSplit[1]);
  }

  public readonly getServiceText = computed(() => this.serviceText());

  public back(): void {
    this.pageLocation.back();
  }

  public setLocation(): void {
    const location = this.store.meraLocation;
    if (!location.city_name && this.retryed < MAX_LOCATION_RETRIES) {
      setTimeout(() => {
        this.retryed += 1;
        console.info(`Retryed location: ${String(this.retryed)}`);
        this.setLocation();
      }, LOCATION_RETRY_DELAY_MS);
    } else if (!location.city_name && this.retryed >= MAX_LOCATION_RETRIES) {
      this.store.setDefaultLocation();
    }
    if (location.city_name) {
      const locInput = this.locationInput?.nativeElement;
      let locList: any = [];
      const label = this.label();
      if (label) {
        label.split(', ').forEach((key: any) => {
          locList.push(this.store.meraLocation?.[key]);
        });
      } else {
        locList = [this.store.meraLocation.city_name, this.store.meraLocation.state_name].filter(
          (value) => value !== null && value !== undefined && value !== ''
        );
      }
      if (locInput) {
        locInput.value = locList.join(', ');
      }
      this.locationText.set(locList.join(', '));
      this.locationSlug = toUrlSlug(location.city_name);
      this.postCodeSlug = (location.post_code || '').toLowerCase();
    }
    this.cd.markForCheck();
  }

  public readonly filteredPopularCategories = computed<IPopularServiceDatum[]>(() => {
    const serviceText = this.serviceText();
    const popularCategories = this.popularCategories();
    if (serviceText) {
      return popularCategories.filter((c) => {
        return c.name.toLowerCase().includes(serviceText.toLowerCase());
      });
    }
    return popularCategories;
  });

  public filteredAllServices(): IAvaanaCategory[] {
    if (this.serviceText()) {
      return this.allServices.filter((c) => {
        return c.name.toLowerCase().includes(this.serviceText().toLowerCase());
      });
    }
    return this.allServices;
  }

  // filteredBusinesses() {
  //   if (this.serviceText) {
  //     return this.businessList.filter((c) => {
  //       return c.name.toLowerCase().includes(this.serviceText.toLowerCase());
  //     });
  //   }
  //   return [];
  // }

  // filteredHealthConditions() {
  //   if (this.serviceText) {
  //     return this.healthConditions.filter((c) => {
  //       return c.name.toLowerCase().includes(this.serviceText.toLowerCase());
  //     });
  //   }
  //   return [];
  // }

  // Fetching Data

  public fetchPopularCategories(): void {
    const popularCategoriesSub = this.api.getPopularList().subscribe({
      next: (response: any) => {
        this.popularCategories.set(response.data);
        this.cd.markForCheck();
      }
    });
    this.subscriptions.push(popularCategoriesSub);
  }

  public fetchAllServices(): void {
    const allServicesSub = this.api.getAllServices().subscribe({
      next: (response: any) => {
        this.allServices = response.data.sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.cd.markForCheck();
      }
    });
    this.subscriptions.push(allServicesSub);
  }

  // fetchAllBusinessList() {
  //   this.api.getAllAvailableBusiness().subscribe({
  //     next: (response) => {
  //       this.businessList = response.data;
  //     }
  //   });
  // }

  // fetchAllHealthConditions() {
  //   this.api.getHealthConditions().subscribe({
  //     next: (response) => {
  //       this.healthConditions = response;
  //     }
  //   });
  // }

  public searchInput(): void {
    if (this.serviceText() === '') {
      this.searchType.set('');
      this.searchSlug = '';
    }
  }

  public dateChanged(e: any): void {
    this.selectedDate.set(moment(e.value));
  }

  public openDropdown(dropdownType: string): void {
    this.dropdownType.set(dropdownType);
  }

  public selectOption(name: string, searchType: string): void {
    this.serviceText.set(name);
    this.dropdownType.set('');
    this.searchType.set(this.searchType() !== 'online' ? searchType : 'online');
    this.searchSlug = toUrlSlug(name);
  }

  public selectBizOption(biz: any, searchType: string): void {
    this.serviceText.set(biz.name);
    this.dropdownType.set('');
    this.searchType.set(this.searchType() !== 'online' ? searchType : 'online');
    this.searchSlug = biz.slug;
    this.locationSlug = toUrlSlug(biz.city);
  }

  public selectLocation(location: ILocationDropdownList, desktop = false): void {
    this.locationText.set(location.displayText);
    const locationInput = document.getElementById('location-input') as HTMLInputElement;
    if (locationInput) {
      locationInput.value = location.displayText;
    }
    this.dropdownType.set('');
    this.getReverseGeoCodeByAddress(location.prediction, desktop);
  }

  public getmeraLocation(): void {
    this.store.fetchmeraLocation.next(true);
    this.dropdownType.set('');
  }

  public changeToOnline(): void {
    this.locationText.set('Online');
    this.searchType.set('online');
    const locationInput = document.getElementById('location-input') as HTMLInputElement;
    if (locationInput) {
      locationInput.value = 'Online';
    }
    this.dropdownType.set('');
  }

  public onLocationTextChanged(e: Event): void {
    const inputElement = e.target as HTMLInputElement;
    const locInput = inputElement.value;
    if (locInput === '' || locInput === null) {
      this.clearLocation();
      return;
    }
    this.locationTextChange.next(locInput);
  }

  public clearLocation(): void {
    this.filteredLocationsList.set([]);
  }

  public getReverseGeoCodeByAddress(prediction: Prediction, desktop = false): void {
    const address = prediction.description;
    this.fetchingLocation.set(true);
    const responseObs = this.api.getGeoCodingByAddress(address);
    responseObs.subscribe({
      next: (response: any) => {
        let state = '';
        let city = '';
        try {
          let postcode = '';

          if (response.geocode_details.results[0].address_components) {
            for (let i = 0; i < response.geocode_details.results[0].address_components.length; i++) {
              const component = response.geocode_details.results[0].address_components[i];
              if (component.types[0] === 'postal_code') {
                postcode = component.long_name;
              } else if (component.types[0] === 'administrative_area_level_1') {
                state = component.long_name;
              } else if (
                component.types[0] === 'locality' ||
                (component.types.length > LOCALITY_TYPES_MIN_LENGTH_LEVEL_2 && component.types[1] === 'locality') ||
                (component.types.length > LOCALITY_TYPES_MIN_LENGTH_LEVEL_3 &&
                  component.types[LOCALITY_TYPE_INDEX_LEVEL_3] === 'locality')
              ) {
                city = component.long_name;
              }
            }
          }
          if (postcode === '') {
            prediction.postal_code = this.setMajorCityAndSuburbInfo(city);
          } else {
            prediction.postal_code = postcode;
          }
          const lng = response.geocode_details.results[0].geometry.location.lng;
          const lat = response.geocode_details.results[0].geometry.location.lat;
          prediction.longitude = lng;
          prediction.latitude = lat;
        } catch (e) {
          console.info(e);
        }
        this.toSetLocation.lat = prediction.latitude;
        this.toSetLocation.lng = prediction.longitude;
        this.toSetLocation.post_code = prediction.postal_code;
        this.toSetLocation.city_name = city;
        this.toSetLocation.state_name = state;

        this.locationSlug = toUrlSlug(city);
        this.postCodeSlug = prediction.postal_code;
        this.fetchingLocation.set(false);

        if (this.toSetLocation?.city_name && this.toSetLocation.state_name && desktop) {
          this.store.setmeraLocation(this.toSetLocation);
        }
        this.cd.markForCheck();
      }
    });
  }

  public setMajorCityAndSuburbInfo(suburb: string): string {
    const mc = suburb.toLowerCase();
    if (mc === 'brisbane') {
      return '4000';
    } else if (mc === 'perth') {
      return '6000';
    } else if (mc === 'melbourne') {
      return '3000';
    } else if (mc === 'adelaide') {
      return '5000';
    } else if (mc === 'sydney') {
      return '2000';
    }
    return '';
  }

  public searchNow(): void {
    const browseOrSearch = this.router.url?.split('/')?.[1] || 'browse';
    let url = '';
    const queryParams: any = {};
    if (this.searchType() === 'online') {
      url = `/online/${this.searchSlug}`;
    } else {
      if (this.searchType() === 'business') {
        url = `/provider/${this.searchSlug}/${this.locationSlug}`;
      } else if (this.searchType() === 'service') {
        url = `/${browseOrSearch}/${this.searchSlug}/${this.locationSlug}`;
      } else if (this.searchType() === 'health_condition') {
        url = `/${browseOrSearch}/health-condition/${this.searchSlug}/${this.locationSlug}`;
      } else {
        url = `/${browseOrSearch}/health-and-wellbeing-services/${this.locationSlug}`;
      }
      if (this.searchType() !== 'business' && this.searchType() !== 'online' && this.postCodeSlug) {
        url += `/${this.postCodeSlug}`;
      }
      if (this.searchType() !== 'business' && this.selectedDate()) {
        queryParams.dt = this.selectedDate().format('YYYY-MM-DD');
      }
    }
    if (this.toSetLocation?.city_name && this.toSetLocation.state_name) {
      this.store.setmeraLocation(this.toSetLocation);
    }
    this.router.navigate([url], {
      queryParams: this.extraParams
    });
  }

  public locationFocus(): void {
    this.locationInput.nativeElement.value = '';
    this.locationText.set('');
  }

  public locationFocusOut(e: Event): void {
    if ((e.target as HTMLInputElement).value === '') {
      let locList: any = [];
      const label = this.label();
      if (label) {
        label.split(', ').forEach((key: any) => {
          locList.push(this.store.meraLocation?.[key]);
        });
      } else {
        locList = [this.store.meraLocation.city_name, this.store.meraLocation.state_name].filter(
          (value) => value !== null && value !== undefined && value !== ''
        );
      }
      this.locationText.set(locList.join(', '));
      this.locationInput.nativeElement.value = locList.join(', ');
    }
  }
  public openMobilePop(): void {
    const dialogData: MatDialogConfig = {
      disableClose: false,
      hasBackdrop: true,
      width: '100%',
      maxWidth: '400px',
      height: '100%',
      maxHeight: '400px',
      panelClass: ['searchboxPanel', 'popup-container'],
      data: {
        service: this.serviceText(),
        location: this.locationText(),
        date: this.selectedDate(),
        params: this.extraParams
      }
    };
    this.dialog.open(SearchBoxPopComponent, dialogData);
  }

  public changeCurrentView(): void {
    this.currentView = this.currentView === 'list' ? 'map' : 'list';
    this.store.currentPageType.next(this.currentView);
  }

  private readonly documentClickListener = (event: MouseEvent): void => {
    const dropdowns = document.querySelectorAll('.search-input-box');
    if (dropdowns) {
      let opened = false;
      dropdowns.forEach((dropdown: Element) => {
        if (dropdown && !dropdown.contains(event.target as Node)) {
          dropdown.classList.remove('show');
        }
        if (dropdown.classList.contains('show')) {
          opened = true;
        }
      });
      if (!opened) {
        this.dropdownType.set('');
        this.cd.markForCheck();
      }
    }
  };

  private getPlaces(value: string): void {
    this.filteredLocationsList.set([]);
    void this.getPlacePredictions(value).then(
      (suggestions) => {
        for (const suggestion of suggestions) {
          const placePrediction = suggestion.placePrediction;
          if (!placePrediction) {
            continue;
          }
          const description = placePrediction.text.text;
          const idx = description.indexOf(', Australia');
          let locationDisplayText = description;
          if (idx !== -1) {
            locationDisplayText = description.substring(0, idx);
          }

          const prediction = new Prediction();
          prediction.description = description;
          prediction.place_id = placePrediction.placeId;
          prediction.structured_formatting = { main_text: placePrediction.mainText?.text ?? locationDisplayText };

          const item = {
            text: description,
            displayText: locationDisplayText,
            place_id: placePrediction.placeId,
            prediction
          };
          this.filteredLocationsList.update((list) => [...list, item]);
        }
        this.cd.markForCheck();
      },
      (error: unknown) => {
        console.info(error);
      }
    );
  }

  private async getPlacePredictions(query: string): Promise<google.maps.places.AutocompleteSuggestion[]> {
    this.filteredLocationsList.set([]);
    await this.googleMapsLoader.whenReady();
    const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      includedPrimaryTypes: ['geocode'],
      includedRegionCodes: ['AU']
    });
    return response.suggestions;
  }
}
